import { useCallback, useRef } from 'react';

import { useEffectOnce } from 'usehooks-ts';

import { adaptPlaceForBackend, adaptPlaceForFrontEnd, formatGeocodingOutput, GMAPS_REQUESTED_FIELDS, GMAPS_REQUESTED_TYPES } from '../utils/mapsUtil';

const useFetchGmapsPlace = () => {
  // trinary: not started / loading / loaded
  const isPlaceServiceLoaded = useRef(null);
  const mapsSessionTokenRef = useRef(null);
  const mapsAutocompleteSuggestionRef = useRef(null);
  const mapsPlaceRef = useRef(null);
  const geocoderInstance = useRef(null);

  useEffectOnce(() => {
    loadPlacesApi();
  });

  const loadPlacesApi = () => {
    if (!window.google) {
      console.warn('Google not ready');
      return;
    }

    // already in loading or loaded
    if (isPlaceServiceLoaded.current != null) {
      return;
    }

    isPlaceServiceLoaded.current = false;
    if (mapsSessionTokenRef.current != null) {
      isPlaceServiceLoaded.current = true;
      return;
    }

    window.google.maps
      .importLibrary('places')
      .then(({ AutocompleteSuggestion, AutocompleteSessionToken, Place }) => {
        mapsSessionTokenRef.current = new AutocompleteSessionToken();
        mapsAutocompleteSuggestionRef.current = AutocompleteSuggestion;
        mapsPlaceRef.current = Place;
        isPlaceServiceLoaded.current = true;
      })
      .catch(err => console.error('Error loadPlacesApi:', err));
  };

  const loadGeocoderApi = onLoad => {
    if (!isPlaceServiceLoaded.current) return;
    if (geocoderInstance.current != null) return onLoad();

    window.google.maps
      .importLibrary('geocoding')
      .then(({ Geocoder }) => {
        geocoderInstance.current = new Geocoder();
        onLoad();
      })
      .catch(err => console.error('Error loadGeocoderApi:', err));
  };

  const getLatLngFromName = (nameToSearch, onPlaceResult) => {
    const PlaceClass = mapsPlaceRef.current;
    PlaceClass.searchByText({
      textQuery: nameToSearch,
      fields: GMAPS_REQUESTED_FIELDS,
      maxResultCount: 1,
    })
      .then(response => {
        const place = response.places != null && response.places.length > 0 ? response.places[0] : null;
        onPlaceResult(adaptPlaceForBackend(place));
      })
      .catch(err => {
        console.error('Error getLatLngFromName:', err);
        onPlaceResult(null);
      });
  };

  const getNameFromLatLng = (latLngValue, onPlaceResult) => {
    loadGeocoderApi(() => {
      geocoderInstance.current.geocode({ location: adaptPlaceForFrontEnd(latLngValue) }, (results, status) => {
        if (status === 'OK' && results[0]) {
          onPlaceResult(formatGeocodingOutput(results[0]));
        } else {
          onPlaceResult(null);
        }
      });
    });
  };

  const getPlaceFromSuggestion = (suggestion, onPlaceResult) => {
    const place = suggestion.placePrediction.toPlace();
    place
      .fetchFields({
        fields: GMAPS_REQUESTED_FIELDS,
      })
      .then(placeResult => onPlaceResult(placeResult.place))
      .catch(err => {
        console.error('Error getPlaceFromSuggestion:', err);
        onPlaceResult(null);
      });
  };

  const getPlacesPrediction = useCallback(async input => {
    try {
      const request = {
        input,
        sessionToken: mapsSessionTokenRef.current,
        includedPrimaryTypes: GMAPS_REQUESTED_TYPES,
      };

      const { suggestions } = await mapsAutocompleteSuggestionRef.current.fetchAutocompleteSuggestions(request);
      const options = (suggestions || [])
        .map(s => {
          if (s.placePrediction) {
            const p = s.placePrediction;
            return {
              label: p.text.toString(),
              value: p.placeId,
              suggestion: s,
            };
          }

          if (s.queryPrediction) {
            const q = s.queryPrediction;
            return {
              label: q.text.toString(),
              value: q.text.toString(),
              suggestion: s,
            };
          }

          return null;
        })
        .filter(Boolean); // removes null values returned

      return options;
    } catch (err) {
      console.error('Error getPlacesPrediction:', err);
      return [];
    }
  }, []);

  return { isPlaceServiceLoaded, getLatLngFromName, getNameFromLatLng, getPlaceFromSuggestion, getPlacesPrediction };
};

export default useFetchGmapsPlace;
